import { ActionReducerMapBuilder, AsyncThunk } from "@reduxjs/toolkit";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "./store";

// here we don't want auto-refresh, so we don't extend FetchableState
export interface PaginatedCollectionState {
    ids?: string[];
    link: string | null
    currentRequestId?: string;
    lastFetched?: number;
    error?: any;
}

export interface PaginatedCollectionHookResult {
    ids: string[] | undefined
    haveMore: boolean
    loading: boolean
    more: () => void
    refresh: () => void
}

export function addPaginatedCollectionReducers<
    T extends { id: string },
    Payload extends { link: string | null },
    ThunkArgs extends { reload: boolean },
    State extends PaginatedCollectionState
>(
    builder: ActionReducerMapBuilder<any>,
    payloadSelector: (payload: Payload) => T[],
    thunk: AsyncThunk<Payload, ThunkArgs, any>,
    reducerIntercept?: (type: any, state: State, action: any) => void,
    // moreAction?: ActionCreatorWithPayload<any>,
    // refreshAction?: ActionCreatorWithPayload<any>,
) {
    builder.addCase(thunk.pending, (state: State, action) => {
        if (state.currentRequestId)
            return;
        state.currentRequestId = action.meta.requestId;
        if (action.meta.arg.reload) {
            state.ids = [];
            state.link = null;
        }
        reducerIntercept?.(thunk.pending, state, action);
    })
    builder.addCase(thunk.fulfilled, (state: State, action) => {
        if (state.currentRequestId !== action.meta.requestId)
            return;
        state.currentRequestId = undefined;
        const newItems = payloadSelector(action.payload);
        state.ids = state.ids!.concat(newItems.map(item => item.id));
        state.link = action.payload.link;
        state.lastFetched = Date.now();
        reducerIntercept?.(thunk.fulfilled, state, action);
    })
    builder.addCase(thunk.rejected, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId)
            return;
        state.currentRequestId = undefined;
        reducerIntercept?.(thunk.rejected, state, action);
    })

    // 
    // if (refreshAction) {
    //     builder.addCase(refreshAction, (state) => {
    //         const collection = collectionSelector(state);
    //         if (collection && collection.currentRequestId)
    //             return;
    //         if (collection) {
    //             collection.pages = [];
    //             collection.haveMore = true;
    //         }
    //     })
    // }
}

interface UsePaginatedCollectionParams {
    selector: (state: RootState) => PaginatedCollectionState;
    moreAction: any;
    reloadAction: any;
}

export function usePaginatedCollection({
    selector,
    moreAction,
    reloadAction,
}: UsePaginatedCollectionParams): PaginatedCollectionHookResult {
    const dispatch = useDispatch<AppDispatch>();
    const { ids, link, currentRequestId, lastFetched } = useSelector(selector);
    const haveMore = ids === undefined || link !== null;

    useEffect(() => {
        if (lastFetched === undefined)
            dispatch(reloadAction)
    }, [lastFetched, dispatch, reloadAction]);

    return {
        ids,
        haveMore,
        loading: currentRequestId !== undefined,
        more: () => {
            if (haveMore && !currentRequestId)
                dispatch(moreAction)
        },
        refresh: () => {
            if (!currentRequestId)
                dispatch(reloadAction)
        }
    }
}
